import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Icon } from '@mysaiva/core'
import styles from './ApiKeys.module.scss'

interface SecretBoxProps {
  secret: string
  copyLabel: string
  copiedLabel: string
  onCopied?: () => void
  'data-testid'?: string
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  try {
    textarea.select()
    // execCommand reports a refused copy with `false` rather than throwing.
    if (!document.execCommand('copy')) throw new Error('Copy command was refused')
  } finally {
    document.body.removeChild(textarea)
  }
}

/** Dark one-time secret box with the blue copy button from the design. */
export function SecretBox({
  secret,
  copyLabel,
  copiedLabel,
  onCopied,
  'data-testid': testId = 'secret-box'
}: SecretBoxProps) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(timer.current), [])

  const handleCopy = useCallback(async () => {
    try {
      await copyText(secret)
      setCopied(true)
      onCopied?.()
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }, [secret, onCopied])

  return (
    <div className={styles.secretBox} data-testid={testId}>
      <code className={styles.secretValue}>{secret}</code>
      <button
        type="button"
        className={clsx(styles.copyButton, copied && styles.copied)}
        onClick={handleCopy}
        aria-label={copied ? copiedLabel : copyLabel}
        data-testid={`${testId}-copy`}
      >
        <Icon icon={copied ? 'Check' : 'Copy'} size={20} />
      </button>
    </div>
  )
}
